import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import api from "../../lib/api";
import { AdminHeader, Btn, DataTable, Card, Input, Localized } from "../../components/admin/Fields";
import ImageUpload from "../../components/admin/ImageUpload";
import SEOFields from "../../components/admin/SEOFields";

const empty = () => ({
  slug: "",
  name: { tr: "", en: "", ar: "" },
  tagline: { tr: "", en: "", ar: "" },
  description: { tr: "", en: "", ar: "" },
  long_description: { tr: "", en: "", ar: "" },
  cover_image: "",
  order: 0,
  seo: {},
});

const AdminBusinessUnitsPage = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/business-units").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing?.new) await api.post("/admin/business-units", payload);
      else await api.put(`/admin/business-units/${editing.id}`, payload);
      toast.success("Kaydedildi");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`"${r.slug}" silinsin mi?`)) return;
    await api.delete(`/admin/business-units/${r.id}`);
    load();
  };

  if (editing) {
    return (
      <div data-testid="admin-bu-form">
        <AdminHeader
          eyebrow="Businesses"
          title={editing.new ? "Yeni Faaliyet Alanı" : "Düzenle"}
          action={<div className="flex gap-3"><Btn variant="ghost" onClick={() => setEditing(null)}>İptal</Btn><Btn onClick={save} disabled={busy} testId="bu-save">Kaydet</Btn></div>}
        />
        <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card title="Genel">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="bu-slug" />
                <Input label="Sıra" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
              </div>
              <div className="mt-6"><Localized label="Ad" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="bu-name" /></div>
              <div className="mt-6"><Localized label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} /></div>
              <div className="mt-6"><Localized label="Kısa Açıklama" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea /></div>
              <div className="mt-6"><Localized label="Uzun Açıklama" value={form.long_description} onChange={(v) => setForm({ ...form, long_description: v })} textarea /></div>
            </Card>
          </div>
          <div className="space-y-6">
            <Card title="Görsel"><ImageUpload label="Kapak Görseli" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testId="bu-cover-upload" /></Card>
            <SEOFields
              value={form.seo}
              onChange={(seo) => setForm({ ...form, seo })}
              context={{
                title: form.name?.tr || form.name?.en || form.slug,
                content: [form.tagline?.tr, form.description?.tr, form.long_description?.tr, form.description?.en].filter(Boolean).join(" \n").slice(0, 3000),
                category: "business_unit",
                slug: `businesses/${form.slug}`,
              }}
            />
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-bu-page">
      <AdminHeader eyebrow="Businesses" title="Faaliyet Alanları" action={<Btn onClick={() => { setEditing({ new: true }); setForm(empty()); }} testId="new-bu"><Plus size={14} /> Yeni</Btn>} />
      <DataTable
        columns={[
          { key: "name", label: "Ad", render: (r) => r.name?.tr || r.slug },
          { key: "slug", label: "Slug" },
          { key: "order", label: "Sıra" },
          { key: "actions", label: "", render: (r) => (
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => { setEditing(r); setForm({ ...empty(), ...r }); }}>Düzenle</Btn>
              <Btn variant="danger" onClick={() => remove(r)}><Trash2 size={12} /></Btn>
            </div>
          ) },
        ]}
        rows={rows}
      />
    </div>
  );
};

export default AdminBusinessUnitsPage;
